/**
 * @file lib/auth-context.ts
 * @description 全局認證狀態管理（Zustand Store）。
 *
 * 用途：
 * - 跨組件共享當前登入用戶信息
 * - AuthProvider 在頁面加載時從 /api/auth/me 拉取，並填入 store
 * - 登入/登出時由相應頁面更新 user 字段
 *
 * 持久化策略：
 *  - 只 persist `user` 欄位到 localStorage，讓「刷新頁面」時可立即還原 UI 狀態
 *    （避免守衛 beforeLoad 看到空 user 而誤跳 /login，也避免一閃即過的 login 頁）。
 *  - 不持久化 access/refresh token —— token 仍由後端 httpOnly cookie 管理，
 *    任何 XSS 都偷不到；前端只信任 cookie 為準，user 物件僅作為 UI hint。
 *  - persist 的 user 物件「樂觀」地用，但每次應用啟動仍會打一次 /api/auth/me 校驗，
 *    若 cookie 已失效則清掉 user。
 *
 * 使用範例：
 * ```ts
 * const user = useAuthStore((s) => s.user);
 * const setUser = useAuthStore((s) => s.setUser);
 * ```
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { WorkbenchType } from '@zhcb/shared';

/**
 * 前端使用的認證用戶信息型別。
 * 對應 GET /api/auth/me 回應中的 user 字段。
 *
 * Task 7（動態 RBAC）：
 * - 新增 `capabilities`、`workbenchType`、`menuHidden` 三個可選欄位，
 *   由後端 login / refresh / GET /api/auth/me 攜帶（上線前的舊 persist 資料沒有這些欄位）。
 * - `userCan(user, cap)` 在 `capabilities` 存在時以之為準，否則回落 roles → DEFAULT_ROLE_CAPABILITIES，
 *   確保舊 localStorage user 零行為變化。
 * - `primaryRole` / `role` / `roles` 放寬為 `string`/`string[]`，支持自定義角色代碼（非 UserRole 枚舉）。
 */
export interface AuthUser {
  id: number;
  username: string;
  name: string;
  email: string;
  /**
   * 主角色（選單／工作台分發、身份展示用）。
   * 多角色模型：等於 roles 中依業務優先序挑出的代表角色。
   * 類型放寬為 string 以支持自定義角色代碼。
   */
  primaryRole: string;
  /**
   * 角色能力清單（多角色）。可見性／能力判斷請改用 `userCan(user, cap)`。
   * 類型放寬為 string[] 以支持自定義角色代碼。
   */
  roles: string[];
  /**
   * @deprecated 相容欄位，等於 primaryRole。分發／展示可用；能力判斷請改用 userCan。
   */
  role: string;
  avatar?: string | null;
  /**
   * 動態能力集（DB 動態 RBAC，Task 7 新增，可選）。
   * 後端 login / refresh / /me 攜帶；存在時 userCan 以之為準，不再查角色矩陣。
   * 舊 localStorage persist 沒有此欄位 → userCan 自動回落 roles。
   */
  capabilities?: string[];
  /**
   * 工作台分發類型（可選），決定登入後顯示哪個工作台。
   * 由後端動態 RBAC 計算，前端僅作 UI 分發參考。
   */
  workbenchType?: WorkbenchType;
  /**
   * 需要在菜單中隱藏的菜單項 key 列表（可選）。
   * 由後端動態 RBAC 計算，Task 9（菜單註冊表）實際使用。
   */
  menuHidden?: string[];
}

export function getPersistedAuthUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem('zhcb-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { user?: AuthUser | null } };
    return parsed.state?.user ?? null;
  } catch {
    return null;
  }
}

/**
 * 認證狀態介面。
 */
interface AuthState {
  /** 當前登入用戶，null 表示未登入 */
  user: AuthUser | null;
  /** 是否正在初始化（首次加載 /api/auth/me 期間為 true） */
  loading: boolean;
  /** 更新用戶信息 */
  setUser: (u: AuthUser | null) => void;
  /** 更新加載狀態 */
  setLoading: (b: boolean) => void;
}

/**
 * 全局認證 Store（含 localStorage persist）。
 *
 * 設計：
 *  - `loading` 不持久化：每次應用啟動時都應從 true 開始，由 AuthProvider 完成 /me 校驗後改 false
 *  - `user` 持久化到 localStorage（key: zhcb-auth）：刷新時 UI 立即可用，避免守衛誤跳 login
 *
 * 建議透過 selector 訂閱，避免不必要的重渲染：
 * ```ts
 * const user = useAuthStore((s) => s.user);
 * ```
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      loading: true,
      setUser: (user) => set({ user }),
      setLoading: (loading) => set({ loading }),
    }),
    {
      name: 'zhcb-auth',
      storage: createJSONStorage(() => localStorage),
      // 只持久化 user，loading 每次啟動都從 true 開始
      partialize: (state) => ({ user: state.user }),
    },
  ),
);
